import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CanEdit } from "@/shared/readOnlyManager";
import axios from "axios";
import { useTable } from "react-table";
import { Table } from "reactstrap";
import { useTranslation } from "react-i18next";

import ModalWrapper from "./ModalWrapper";
import { getTeams } from "@/services/team/getTeams";
import { getTeam } from "@/services/team/getTeam";
import {
  useDispatch,
  flashError,
  flashSuccess,
} from "@/shared/components/flash";

const DEFAULT_AVATAR = `${process.env.PUBLIC_URL}/theme/classic/img/avatar/avatar.jpg`;

const getTeamPhotoSrc = (photo) => {
  if (!photo) return null;
  if (String(photo).startsWith("http")) return photo;
  return `${process.env.REACT_APP_API_STORAGE_URL}/${photo}`;
};

const getInitials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "TM";

const Teams = ({ program, organization, refreshToken = 0 }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [teams, setTeams] = useState([]);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setOpen] = useState(false);
  const [modalName, setModalName] = useState(null);
  const [busyTeamId, setBusyTeamId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const closeModal = useCallback(() => {
    setModalName(null);
    setTeam(null);
    setOpen(false);
  }, []);

  const openModal = useCallback((name) => {
    setModalName(name);
    setOpen(true);
  }, []);

  const loadTeams = useCallback(async () => {
    if (!organization?.id || !program?.id) {
      setTeams([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const items = await getTeams(organization.id, program.id);
      setTeams(Array.isArray(items) ? items : []);
    } catch (error) {
      setTeams([]);
      setErrorMessage("Unable to load team members for this program.");
    } finally {
      setLoading(false);
    }
  }, [organization?.id, program?.id]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams, refreshToken]);

  const handleTeamSaved = useCallback(() => {
    closeModal();
    loadTeams();
  }, [closeModal, loadTeams]);

  const onClickEditTeam = useCallback(
    async (teamId) => {
      try {
        setBusyTeamId(teamId);
        const item = await getTeam(organization.id, program.id, teamId);
        setTeam(item);
        openModal("EditTeam");
      } catch (error) {
        flashError(dispatch, error?.response?.data || "Unable to load teammate.");
      } finally {
        setBusyTeamId(null);
      }
    },
    [dispatch, openModal, organization?.id, program?.id]
  );

  const onClickViewTeam = useCallback(
    async (teamId) => {
      try {
        setBusyTeamId(teamId);
        const item = await getTeam(organization.id, program.id, teamId);
        setTeam(item);
        openModal("ViewTeam");
      } catch (error) {
        flashError(dispatch, error?.response?.data || "Unable to load teammate.");
      } finally {
        setBusyTeamId(null);
      }
    },
    [dispatch, openModal, organization?.id, program?.id]
  );

  const onDeleteTeam = useCallback(
    async (teamId) => {
      try {
        setBusyTeamId(teamId);
        const response = await axios.delete(
          `/organization/${program.organization_id}/program/${program.id}/team/${teamId}`
        );

        if (response.status === 200) {
          flashSuccess(dispatch, "Team was deleted!");
          setTeams((currentTeams) =>
            currentTeams.filter((item) => String(item.id) !== String(teamId))
          );

          if (String(team?.id) === String(teamId)) {
            closeModal();
          }
        }
      } catch (error) {
        flashError(dispatch, error?.response?.data || "Unable to delete teammate.");
      } finally {
        setBusyTeamId(null);
      }
    },
    [closeModal, dispatch, program?.id, program?.organization_id, team?.id]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Team Member",
        accessor: "name",
        Cell: ({ row }) => {
          const photoSrc = getTeamPhotoSrc(row.original.photo);

          return (
            <div className="manager-team__member">
              <div className="manager-team__avatar" aria-hidden="true">
                {photoSrc ? (
                  <img
                    src={photoSrc}
                    alt={row.original.name || "Team member"}
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = DEFAULT_AVATAR;
                    }}
                  />
                ) : (
                  <span>{getInitials(row.original.name)}</span>
                )}
              </div>

              <div className="manager-team__member-copy">
                <span className="manager-team__member-name">
                  {row.original.name || "Unnamed teammate"}
                </span>
                <span className="manager-team__member-meta">
                  ID #{row.original.id}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        Header: "Role",
        accessor: "title",
        Cell: ({ value }) => (
          <div className="manager-team__cell-stack">
            <strong>{value || "No title assigned"}</strong>
          </div>
        ),
      },
      {
        Header: "Contact",
        id: "contact",
        Cell: ({ row }) => (
          <div className="manager-team__cell-stack">
            <span>{row.original.contact_email || "No email provided"}</span>
            {row.original.contact_phone && (
              <span className="manager-team__cell-muted">
                {row.original.contact_phone}
              </span>
            )}
          </div>
        ),
      },
      {
        Header: "Description",
        accessor: "description",
        Cell: ({ value }) => (
          <div className="manager-team__description">
            {value || "No description added."}
          </div>
        ),
      },
    ],
    []
  );

  const { getTableProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data: teams,
  });

  const teammateCountLabel = `${teams.length.toLocaleString()} teammate${
    teams.length === 1 ? "" : "s"
  }`;

  return (
    <>
      <div className="manager-team__table-meta">
        <span className="manager-team__table-count">
          {loading ? t("loading") : teammateCountLabel}
        </span>

        {!loading && !errorMessage && teams.length > 0 && (
          <span className="manager-team__table-hint">
            View, edit, or remove teammates for this program.
          </span>
        )}
      </div>

      {loading && (
        <div className="manager-team__empty">Loading team members...</div>
      )}

      {!loading && errorMessage && (
        <div className="manager-team__empty">{errorMessage}</div>
      )}

      {!loading && !errorMessage && teams.length === 0 && (
        <div className="manager-team__empty">
          No team members have been added to this program yet.
        </div>
      )}

      {!loading && !errorMessage && teams.length > 0 && (
        <div className="manager-team__table-shell">
          <Table borderless className="manager-team__table table--stack-mobile" {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()}>{column.render("Header")}</th>
                  ))}
                  <th>Actions</th>
                </tr>
              ))}
            </thead>

            <tbody>
              {rows.map((row) => {
                prepareRow(row);
                const isBusy = busyTeamId === row.original.id;

                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        data-label={typeof cell.column.Header === "string" ? cell.column.Header : ""}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}

                    <td data-label="Actions">
                      <div className="manager-team__actions">
                        <button
                          type="button"
                          className="manager-team__action-btn"
                          onClick={() => onClickViewTeam(row.original.id)}
                          disabled={isBusy}
                        >
                          View
                        </button>

                        <CanEdit>
                          <button
                            type="button"
                            className="manager-team__action-btn"
                            onClick={() => onClickEditTeam(row.original.id)}
                            disabled={isBusy}
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            className="manager-team__action-btn is-danger"
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Are you sure you want to delete this teammate?"
                                )
                              ) {
                                onDeleteTeam(row.original.id);
                              }
                            }}
                            disabled={isBusy}
                          >
                            Delete
                          </button>
                        </CanEdit>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      <ModalWrapper
        name={modalName}
        isOpen={isOpen}
        toggle={closeModal}
        team={team}
        setTeam={setTeam}
        onSaved={handleTeamSaved}
      />
    </>
  );
};

export default Teams;
